import Vue from 'vue';
import { ticketStyle, computedStyles } from 'bto-models/print';
import { BETSLIP_STATUS } from 'bto-models/betslip/const';
import PrintTicket from './print-ticket';
import { PrintIframeService } from './print-iframe.service';
import { IMAGE_KEYS, ImageStorageService } from '@bto-services/storage/image-storage.service';
import PrintDataService from '@bto-services/api/shared/print-data-service';

/**
 * @typedef {import('bto-models/mybets/model-mybet-details').default} MyBetDetailsModel 
 */

/**
 * Handle betslip print
 */
export default class BetslipPrintService {
  constructor(ticketWidth = '') {
    /**
     * Ticket width
     * @type {string}
     */
    this.ticketWidth = ticketWidth;
    /**
     * Print logo image
     * @type {String}
     */
    this.logo = '';
    /**
     * Compact print style
     * @type {boolean}
     */
    this.compactBetslipPrint = APP_SETTINGS.print.compactBetslipPrint;
    /**
     * Betslip group id
     * @type {null}
     */
    this.betslipGroupId = null;
    /**
     * Ticket info
     */
    this.ticketInfo = {
      totalStake: 0,
      totalBonus: 0,
      totalWinnings: 0,
      totalPotWinnings: 0,
      totalTax: 0,
      actualWinnings: 0,
      totalOdds: 0
    };
    this.printCombData = [];
    /**
     * Should we print combinations or not
     * @type {boolean}
     */
    this.printCombinations = false;
    /**
     *  reprint, canceled, paid
     */
    this.printStatus = '';
    /**
     * is betslip from reservation code
     * @type {boolean}
     */
    this.bookedBs = false;
  }

  /**
   * @private
   * Write html content for print in window
   * @param {MyBetDetailsModel} betDetails
   */
  writeContentInWindow(betDetails) {
    const printIframeService = new PrintIframeService();
    const PrintComp = Vue.extend(PrintTicket);
    const printData = new PrintComp({
      propsData: {
        betDetails,
        betslipGroupId: this.betslipGroupId,
        logo: this.logo,
        ticketWidth: this.ticketWidth,
        printCombinations: this.printCombinations,
        compactBetslipPrint: this.compactBetslipPrint,
        ticketInfo: this.ticketInfo,
        printCombData: this.printCombData,
        printStatus: this.printStatus,
        bookedBs: this.bookedBs,
      },
    });
    const bodyPrintCss = `
      .bto-sb-print-ticket { display: block; }
      .block { display: block; width: 100%; }
    `;
    printIframeService.addComponentStyle(bodyPrintCss);
    printIframeService.addComponentStyle(ticketStyle);
    printIframeService.addComponentStyle(computedStyles);
    printIframeService.printComponent(printData);
  }
  /**
   * @private
   * Set ticket details
   * @param {MyBetDetailsModel} betDetails
   */
  setTicketDetails(betDetails) {
    const ticketTotals = betDetails.combinations.reduce((prev, curr) => {
      const total = {
        totalStake: prev.totalStake += curr.stake,
        totalBonus: prev.totalBonus += curr.bonus,
        totalWinnings: prev.totalWinnings += curr.winnings,
        totalPotWinnings: prev.totalPotWinnings += curr.potWinnings,
        totalTax: prev.totalTax += curr.tax,
        totalOdds: prev.totalOdds += curr.odds,
      };
      return total;
    }, {
      totalStake: 0,
      totalBonus: 0,
      totalWinnings: 0,
      totalPotWinnings: 0,
      totalTax: 0,
      totalOdds: 0,
    });
    this.ticketInfo = ticketTotals;
    if (betDetails.combinationData) {
      this.ticketInfo.totalStake = betDetails.combinationData.stakeAmount;
      this.ticketInfo.totalPotWinnings = betDetails.combinationData.maxPotWinnings;
    }
    this.ticketInfo.actualWinnings = this.ticketInfo.totalWinnings ? this.ticketInfo.totalWinnings : this.ticketInfo.totalPotWinnings;
  }

  /**
   * @private
   * Print short version of the betslip for paid and canceled tickets
   * @param {MyBetDetailsModel} betDetails
   */
  useComapctPrint(betDetails) {
    if (!APP_SETTINGS.print.compactBetslipPrint) {
      return false;
    }
    switch (betDetails.betSlipGroupStatusId) {
      case BETSLIP_STATUS.PAID:
      case BETSLIP_STATUS.CANCELLED:
        return true;
      default:
        return false;
    }
  }

  /**
   * @private
   * Handle print data
   * @param {MyBetDetailsModel} betDetails
   */
  handlePrintData(betDetails) {
    // if we don't have print Data, reset the print.
    if (typeof betDetails.betSlipGroupID === 'undefined') {
      return;
    }
    JsBarcode('#barcode', betDetails.identifier, {
      width: parseFloat(APP_SETTINGS.print.barcodeWidth) || 2,
      height: parseFloat(APP_SETTINGS.print.barcodeHeight) || 100,
      textMargin: parseFloat(APP_SETTINGS.print.barcodeTextMargin) || 2,
      margin: parseFloat(APP_SETTINGS.print.barcodeMargin) || 10,
      fontSize: parseFloat(APP_SETTINGS.print.barcodeFontSize) || 20,
    }); // eslint-disable-line
    this.compactBetslipPrint = this.useComapctPrint(betDetails);
    // Gather required ticket data
    this.setTicketDetails(betDetails);
    // Write content in widnow
    this.writeContentInWindow(betDetails);
    // print second copy of the betslip.
    // used only for paid tickets when setting for paymentConfirmation is set to true
    if (APP_SETTINGS.print.paymentConfirmation &&
      betDetails.betSlipGroupStatusId === BETSLIP_STATUS.PAID
    ) {
      this.writeContentInWindow(betDetails);
    }
  }
  /**
   * @private
   * Load print ticket data from api
   */
  loadPrintDataFromApi() {
    let printDataService = new PrintDataService();
    printDataService.getData(this.betslipGroupId).then((data) => {
      this.handlePrintData(data);
    });
  }
  /**
   * @private
   * Set the print content and style to the iFrame
   * @param {MyBetDetailsModel|boolean} printDataObj 
   */
  setPrint(printDataObj = false) {
    if (printDataObj && typeof printDataObj === 'object') {
      this.handlePrintData(printDataObj);
      return;
    }
    this.loadPrintDataFromApi();
  }
  /**
   * @public
   * Print betslip method
   * @param {Event} event
   * @param {number} betslipGroupId
   * @param {MyBetDetailsModel|boolean} [override]
   * @param {boolean} [printCombinations]
   * @param {string} [printStatus]
   * @param {boolean} [bookedBs]
   */
  async printBetslip(event, betslipGroupId, override = false, printCombinations = false, printStatus = '', bookedBs = false) {
    if (event) {
      event.preventDefault();
    }
    const imageStore = new ImageStorageService();
    const imageItem = await imageStore.getImage(IMAGE_KEYS.PRINT_TICKET);
    this.logo = imageItem ? imageItem.image : '';
    this.betslipGroupId = betslipGroupId;
    this.printCombinations = printCombinations;
    this.printStatus = printStatus;
    this.bookedBs = bookedBs;

    this.setPrint(override);
  }
}
